// src/api/inventory/productApi.ts
import { ENV } from '../../config/runtimeEnv';
import type { Product, ProductImage } from '../../types/inventory/ProductTypes';
import { http } from "../../lib/http/client";
const API_BASE_URL = ENV.VITE_COMMERCE_SERVER;

export const fetchProducts = async (
	page: number = 1,
	perPage: number = 20,
	company_id: number,
	search?: string,
	categoryId?: number
): Promise<{ data: Product[]; total: number }> => {
	const response = await http.get(`${API_BASE_URL}/products`, {
		params: {
			page,
			per_page: perPage,
			company_id,
			...(search ? { search } : {}),
			...(categoryId ? { category_id: categoryId } : {}),
		},
	});
	return {
		data: response.data.data,
		total: response.data.pagination.total_items,
	};
};

export const fetchProduct = async (product_id: number): Promise<Product> => {
	const response = await http.get(`${API_BASE_URL}/products/${product_id}`);
	return response.data;
};

export const createProduct = async (productData: Partial<Product>): Promise<Product> => {
	const response = await http.post(
		`${API_BASE_URL}/products`,
		{
			...productData,
		}
	);
	return response.data;
};

export const updateProduct = async (product_id: number, productData: Partial<Product>): Promise<Product> => {
	const response = await http.put(
		`${API_BASE_URL}/products/${product_id}`,
		{
			...productData,
		}
	);
	return response.data;
};

export const deleteProduct = async (product_id: number): Promise<void> => {
	await http.delete(`${API_BASE_URL}/products/${product_id}`);
};

/**
 * Upload multiple images to a product
 */
export const uploadProductImages = async (
	product_id: number,
	files: File[],
	altTexts?: string[]
): Promise<{ images: ProductImage[]; product: Product }> => {
	const formData = new FormData();

	files.forEach((file) => {
		formData.append("files[]", file);
	});

	if (altTexts && altTexts.length > 0) {
		altTexts.forEach((alt, index) => {
			formData.append(`alt_texts[${index}]`, alt);
		});
	}

	const response = await http.post(
		`${API_BASE_URL}/products/${product_id}/images`,
		formData,
		{
			headers: {
				"Content-Type": "multipart/form-data",
			},
		}
	);
	return response.data;
};

/**
 * Delete a product image
 */
export const deleteProductImage = async (
	product_id: number,
	image_id: number
): Promise<void> => {
	await http.delete(`${API_BASE_URL}/products/${product_id}/images/${image_id}`);
};

/**
 * Set an image as primary
 */
export const setPrimaryProductImage = async (
	product_id: number,
	image_id: number
): Promise<{ product: Product }> => {
	const response = await http.put(
		`${API_BASE_URL}/products/${product_id}/images/${image_id}/primary`,
		{}
	);
	return response.data;
};

/**
 * Reorder product images
 */
export const reorderProductImages = async (
	product_id: number,
	image_ids: number[]
): Promise<{ product: Product }> => {
	const response = await http.put(
		`${API_BASE_URL}/products/${product_id}/images/reorder`,
		{ image_ids }
	);
	return response.data;
};

export const getSupplementCatalog = async (companyId: number): Promise<Product[]> => {
	const response = await http.get(`${API_BASE_URL}/products/supplement-catalog`, {
		params: {
			company_id: companyId,
		},
	});
	return response.data;
};